import React, { useMemo, useState, useEffect, useRef } from 'react';
import styles from './NewsPage.module.scss';

import useScrollToTop from '../../../../app/hooks/useScrollToTop';
import endpoints from '../../../../api/endpoints';
import { useGetQuery } from '../../../../services/apiCall';
import NewsCard from '../../../atoms/newsCard/NewsCard';
import HorizCard from '../../../atoms/newsCard/HorizCard';
import Text from '../../../atoms/text/Text';
import Paginator from '../../../atoms/paginator/Paginator';
import Loader from '../../../atoms/loader/Loader';
import { useNavigate } from 'react-router-dom';
import useIsRtl from '../../../../app/helpers/isRtl';
import NewsSearchModal from './news-search-modal/NewsSearchModal';
import NoData from '../../../organism/filterContainer/factory/noData/noData';
import { dir } from 'i18next';
import getCookie from '../../../../app/helpers/getCookie';

const arrowSvg = '/assets/svg/nav-right-arrow.svg';
const homeSvg = '/assets/svg/home.svg';
const searchSvg = '/assets/svg/search.svg';
const filterSvg = '/assets/svg/filter.svg';

const decodeHtml = (s) => {
  if (!s) return '';
  const t = document.createElement('textarea');
  t.innerHTML = s;
  const once = t.value;
  t.innerHTML = once;
  return t.value;
};

export const normalizeItem = (item = {}) => {
  const has = (k) => item?.[k] != null && item?.[k] !== '';

  const isMedia =
    has('mediaGalleryId') || has('galleryId') || Array.isArray(item.images);
  const isNewsletter = has('newsletterDate') || has('newsletterImage');
  const isEvent =
    !isNewsletter &&
    (has('eventId') || has('eventDate') || has('eventText_AR'));

  const pickImage = () => {
    if (has('image')) return `${item.url ?? ''}${item.image}`;
    if (has('newsletterImage'))
      return `${item.url ?? ''}${item.newsletterImage}`;
    if (has('image1') || has('image2') || has('image3')) {
      return `${item.url ?? ''}${item.image1 || item.image2 || item.image3 || ''}`;
    }
    return '';
  };
  if (isMedia) {
    const first = Array.isArray(item.images) ? item.images[0] : null;
    return {
      id: item.mediaGalleryId || item.galleryId || item.id || null,
      date: first?.createdOn || item?.date || null,
      title: item.galleryName_AR || item.title_AR || '',
      description: '',
      image: first ? `${first.url ?? ''}${first.imageURL ?? ''}` : pickImage(),
      type: 'media',
      registerLink: null,
      gallery: {
        images: Array.isArray(item?.images)
          ? item.images.map((img) => `${img.url ?? ''}${img.imageURL ?? ''}`)
          : [],
      },
    };
  }

  if (isNewsletter) {
    return {
      id: item.newsletterId || item.eventId || item.id || null,
      date: item.newsletterDate || item.date || null,
      title: item.title_AR || '',
      description: decodeHtml(item.body_AR) || '',
      image: pickImage(),
      type: 'newsletter',
      registerLink: null,
    };
  }

  if (isEvent) {
    return {
      id: item.eventId || item.id || null,
      date: item.eventDate || item.date || null,
      title: item.title_AR || '',
      description: decodeHtml(item.eventText_AR) || '',
      image: pickImage(),
      type: 'events',
      registerLink: item?.registerLink
        ? /^https?:\/\//i.test(item.registerLink)
          ? item.registerLink
          : `https://${item.registerLink}`
        : null,
    };
  }

  return {
    id: item.newsId || item.id || null,
    date: item.newsDate || item.date || null,
    title: item.title_AR || '',
    description: decodeHtml(item.newsTextAr) || '',
    image: pickImage(),
    type: 'news',
    registerLink: null,
  };
};

export const Breadcrumbs = ({
  isRtl,
  navigate,
  currentTitleKey,
  noBack = true,
  back,
  backTo,
}) => (
  <nav className={styles.breadcrumbs} aria-label="breadcrumb">
    <ul className={`${styles.list} ${isRtl ? styles['rtl-list'] : ''}`}>
      <li className={styles.item} onClick={() => navigate('/')}>
        <img src={homeSvg} alt="" />
        <Text
          title="common.home_page"
          variant="text-regular-14"
          className={styles.link}
        />
      </li>
      {noBack ? (
        <>
          <li className={styles.sep} aria-hidden="true">
            <img src={arrowSvg} alt="" className={styles['arrow']} />
          </li>
          <li
            className={styles.item}
            onClick={() =>
              navigate(backTo ? backTo : '/news-and-events/media-gallery')
            }
          >
            <Text
              title={back ? back : 'common.media_center'}
              variant="text-regular-14"
              className={styles.link}
            />
          </li>
        </>
      ) : (
        ''
      )}

      <li className={styles.sep} aria-hidden="true">
        <img src={arrowSvg} alt="" className={styles['arrow']} />
      </li>
      <li className={`${styles.item} ${styles.current}`} aria-current="page">
        <Text
          title={currentTitleKey}
          variant="text-regular-14"
          className={styles.link}
        />
      </li>
    </ul>
  </nav>
);

export const SortMenu = ({
  isRtl,
  isOpen,
  onToggle,
  sortBy,
  onPick,
  setIsSearchOpen,
}) => {
  const menuRef = useRef(null);
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onToggle(false);
    const onClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) onToggle(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [isOpen, onToggle]);

  return (
    <div className={styles['filters']} dir={isRtl ? 'rtl' : 'ltr'}>
      <button
        type="button"
        className={styles.iconBtn}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => onToggle(!isOpen)}
      >
        <img src={filterSvg} alt="Filter" className={styles['icon']} />{' '}
        <Text
          title="common.sort"
          variant="text-regular-14"
          className={styles.title}
        />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className={`${styles.menu} ${isRtl ? styles['menu-rtl'] : ''}`}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className={`${styles.menuItem} ${sortBy === 'date' ? styles.active : ''}`}
            onClick={() => {
              onPick('date');
              onToggle(false);
            }}
            style={{ textAlign: isRtl ? 'right' : '' }}
          >
            <Text
              title="common.date_newest_first"
              variant="text-regular-14"
              className={styles.title}
            />
          </button>
          <button
            type="button"
            role="menuitem"
            className={`${styles.menuItem} ${sortBy === 'title' ? styles.active : ''}`}
            onClick={() => {
              onPick('title');
              onToggle(false);
            }}
            style={{ textAlign: isRtl ? 'right' : '' }}
          >
            <Text
              title="common.title_a_to_z"
              variant="text-regular-14"
              className={styles.title}
            />
          </button>
        </div>
      )}

      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setIsSearchOpen(true)}
        aria-label="Open search"
      >
        <img src={searchSvg} alt="Search" className={styles['icon']} />
        <Text
          title={isEnglish ? 'Search' : 'common.search'}
          variant="text-regular-14"
          className={styles.title}
        />
      </button>
    </div>
  );
};

const API_BY_TYPE = {
  news: endpoints.news.getNews,
  events: endpoints.news.getEvents,
  media: endpoints.news.mediaGallery,
  'news-letter': endpoints.news.getNewsletter,
};

const TITLES = {
  news: 'News.ALL NEWS',
  events: 'common.events',
  media: 'MediaGallery.MEDIA GALLERY',
  'media-gallery': 'MediaGallery.MEDIA GALLERY',
  newsletter: 'common.newsletter',
  'news-letter': 'common.newsletter',
};

const NewsPage = ({ type = 'news' }) => {
  useScrollToTop();
  const isRtl = useIsRtl();
  const navigate = useNavigate();

  const isMediaType = type === 'media';
  const [sortBy, setSortBy] = useState('date');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filteredTotal, setFilteredTotal] = useState(null);
  const [filteredHeaderTotal, setFilteredHeaderTotal] = useState(null);

  const cardsContainerRef = useRef(null);
  const listTopRef = useRef();
  const headerContainerRef = useRef(null);
  const apiUrl = API_BY_TYPE[type] ?? endpoints.news.getNews;
  const { data, isLoading } = useGetQuery({
    apiUrl,
    ...(isMediaType ? {} : { params: ['1000', '1', '?IsActive=true'] }),
  });

  const rawItems = useMemo(() => {
    if (isMediaType) {
      return Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
    }
    return data?.items ?? [];
  }, [data, isMediaType]);

  const items = useMemo(() => {
    const list = (Array.isArray(rawItems) ? rawItems : []).map(normalizeItem);
    return [...list].sort((a, b) => {
      if (sortBy === 'title') {
        const av = (a.title || '').toString();
        const bv = (b.title || '').toString();
        return av.localeCompare(bv, undefined, { sensitivity: 'base' });
      }
      const ad = new Date(a.date).getTime();
      const bd = new Date(b.date).getTime();
      if (isNaN(ad) && isNaN(bd)) return 0;
      if (isNaN(ad)) return 1;
      if (isNaN(bd)) return -1;
      return bd - ad;
    });
  }, [rawItems, sortBy]);

  useEffect(() => setCurrentPage(1), [sortBy]);

  useEffect(() => {
    if (!listTopRef.current) return;
    listTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage, itemsPerPage]);
  let first, second, rest;

  if (type === 'events') {
    first = items[0];
    rest = items;
  } else if (type === 'news') {
    first = items[0];
    second = items[1];
    rest = items;
  } else {
    rest = items;
  }

  const totalCount = filteredTotal ?? rest.length;
  const pagedRest = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rest.slice(start, start + itemsPerPage);
  }, [rest, currentPage, itemsPerPage]);

  const currentTitleKey = TITLES[type] ?? 'News.ALL NEWS';
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const headerFallback = (first ? 1 : 0) + (type === 'news' && second ? 1 : 0);

  const headerVisible = filteredHeaderTotal ?? headerFallback;

  const showNoData = !isLoading && headerVisible + totalCount === 0;
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);
  return (
    <div className={styles['news-page']}>
      <Breadcrumbs
        isRtl={isRtl}
        navigate={navigate}
        currentTitleKey={currentTitleKey}
      />

      <div className={styles.banner}>
        <Text
          title={TITLES[type] ?? 'News.ALL NEWS'}
          variant="text-italic-bold-30"
          className={styles.header}
        />
      </div>

      <SortMenu
        isRtl={isRtl}
        isOpen={isSortOpen}
        onToggle={setIsSortOpen}
        sortBy={sortBy}
        onPick={setSortBy}
        setIsSearchOpen={setIsSearchOpen}
      />

      {isLoading ? (
        <div style={{ width: '100%', height: '50vh' }}>
          <Loader />
        </div>
      ) : null}

      {(type === 'news' || type === 'events') && (
        <div
          ref={headerContainerRef}
          className={`${styles['header-grid']} ${type === 'events' ? styles['events-header'] : ''}`}
          dir={isRtl ? 'rtl' : ''}
        >
          {first && (
            <div
              className={styles.searchable}
              data-search-item
              data-date={first.date || ''}
              data-type={first.type}
            >
              <HorizCard
                id={first.id}
                image={first.image}
                date={first.date}
                title={first.title}
                description={first.description}
                type={first.type}
                registerLink={first.registerLink}
              />
            </div>
          )}

          {second && type === 'news' && (
            <div
              className={styles.searchable}
              data-search-item
              data-date={second.date || ''}
              data-type={second.type}
            >
              <NewsCard
                id={second.id}
                image={second.image}
                date={second.date}
                title={second.title}
                description={second.description}
                type={second.type}
                variant="faded"
              />
            </div>
          )}
        </div>
      )}

      <div ref={listTopRef} />
      {showNoData ? (
        <div className={styles.noData}>
          <NoData />
        </div>
      ) : (
        <div
          className={styles.cardsWrapper}
          ref={cardsContainerRef}
          dir={isRtl ? 'rtl' : ''}
        >
          {pagedRest.map((item) => (
            <div
              key={item.id}
              className={styles.searchable}
              data-search-item
              data-date={item.date || ''}
              data-type={item.type}
            >
              <NewsCard
                id={item.id}
                image={item.image}
                date={item.date}
                title={item.title}
                description={item.description}
                type={item.type}
                imageWrapper={
                  type === 'news-letter' ? styles['image-wrapper'] : ''
                }
                gallery={item.gallery}
                isCLickable={type === 'media'}
              />
            </div>
          ))}
        </div>
      )}
      <NewsSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        containerRefs={[headerContainerRef, cardsContainerRef]}
        onFiltered={(info) => {
          if (!info) {
            setFilteredHeaderTotal(null);
            setFilteredTotal(null);
            setCurrentPage(1);
            return;
          }
          setFilteredHeaderTotal(info.headerTotal ?? null);
          setFilteredTotal(info.listTotal ?? info.total ?? null);
          setCurrentPage(1);
        }}
      />
      {totalCount > 0 && (
        <Paginator
          currentPage={currentPage}
          totalCount={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
          className={styles.paginator}
        />
      )}
    </div>
  );
};

export default NewsPage;
